import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PublishFromImportInput,
  PublishImportAsFeedInput,
  ResolvedImportPlace,
  SourceMessageDto,
} from '@upme/contracts';
import { api } from '../../app/api-client';
import { importQueueItemByIdOptions, importQueueKey } from './queries';

function invalidateAll(qc: ReturnType<typeof useQueryClient>, id?: string, includeEvents = false) {
  qc.invalidateQueries({ queryKey: [...importQueueKey, 'list'] });
  if (id) qc.invalidateQueries({ queryKey: importQueueItemByIdOptions(id).queryKey });
  // Only invalidate events list when a new Event is actually created (publish).
  if (includeEvents) qc.invalidateQueries({ queryKey: ['admin', 'events', 'list'] });
}

// Backend возвращает { source, events: [...] } — N событий по числу дат во
// входном payload (multi-date поддержка с Task 5).
export interface PublishFromImportResponse {
  source: { id: string; status: string };
  events: Array<{ id: string; title: string; status: string; startsAt: string }>;
}

export interface DuplicateConflictError {
  code: 'duplicate_event' | 'duplicate_news';
  candidates: Array<{ id: string; title: string; startsAt?: string | null | undefined; similarity: number }>;
}

export function isDuplicateConflict(err: unknown): err is { conflict: DuplicateConflictError } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'conflict' in err &&
    typeof (err as Record<string, unknown>).conflict === 'object'
  );
}

export function usePublishFromImport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PublishFromImportInput) => {
      try {
        return await api
          .post(`admin/import-queue/${id}/publish`, { json: body })
          .json<PublishFromImportResponse>();
      } catch (err) {
        // Intercept 409 duplicate conflict — surface as a structured error so
        // the form can show the force-override modal instead of a generic toast.
        if (err instanceof Error && 'response' in err) {
          const res = (err as { response: Response }).response;
          if (res?.status === 409) {
            try {
              const body409 = (await res.clone().json()) as DuplicateConflictError;
              throw { conflict: body409 };
            } catch (jsonErr) {
              if (isDuplicateConflict(jsonErr)) throw jsonErr;
            }
          }
        }
        throw err;
      }
    },
    onSuccess: () => invalidateAll(qc, id, true),
  });
}

export interface PublishImportAsFeedResponse {
  newsId: string;
}

export function usePublishImportAsFeed(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PublishImportAsFeedInput) =>
      api
        .post(`admin/import-queue/${id}/publish-as-feed`, { json: body })
        .json<PublishImportAsFeedResponse>(),
    onSuccess: () => {
      invalidateAll(qc, id);
      qc.invalidateQueries({ queryKey: ['admin', 'news', 'list'] });
    },
  });
}

export function useRejectImportItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { note?: string }) =>
      api.post(`admin/import-queue/${id}/reject`, { json: body }).json<SourceMessageDto>(),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useIgnoreImportItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`admin/import-queue/${id}/ignore`, { json: {} }).json<SourceMessageDto>(),
    onSuccess: () => invalidateAll(qc, id),
  });
}

/**
 * Создаёт/линкует Place из карт-ссылки поста через Google Places (тратит квоту).
 * Возвращает placeId + название — форма подставляет их в поля привязки места.
 */
export function useResolveImportPlace(id: string) {
  return useMutation({
    mutationFn: () => api.post(`admin/import-queue/${id}/resolve-place`).json<ResolvedImportPlace>(),
  });
}

export function useReextractImportItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`admin/import-queue/${id}/reextract`),
    onSuccess: () => invalidateAll(qc, id),
  });
}
